// src/pages/Blog.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  User,
  Eye,
  Tag,
  MessageCircle,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { API_URL, formatDate, getCategoryLabel } from "../lib/utils";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1400&q=80";

// Hero image from public/assets
const HERO_IMAGE = "/assets/hero-partner.png";

// IMPORTANT: build URLs against backend origin (not /api)
const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return FALLBACK_IMAGE;
  const clean = url.trim();
  if (!clean) return FALLBACK_IMAGE;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const CATEGORIES = [
  { value: "all", label: "All Posts" },
  { value: "humanitarian_impact", label: "Humanitarian Impact" },
  { value: "child_welfare", label: "Child Welfare & Education" },
  { value: "rights_inclusion", label: "Rights & Inclusion" },
  { value: "technology_for_good", label: "Technology for Good" },
  { value: "climate_community", label: "Climate & Community" },
  { value: "partner_stories", label: "Partner/Volunteer Stories" },
];

const MiniCalendar = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  const monthName = today.toLocaleString(undefined, { month: "long" });
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const weekLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Card className="rounded-2xl border-stone-300 shadow-sm">
      <CardContent className="p-4">
        <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-700" />
          {monthName} {year}
        </h4>

        <div className="grid grid-cols-7 gap-1 text-[11px] mb-2">
          {weekLabels.map((w) => (
            <div key={w} className="text-center text-stone-600 font-semibold py-1">
              {w}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 text-xs">
          {cells.map((d, idx) => {
            const isToday = d === today.getDate();
            return (
              <div
                key={`${d}-${idx}`}
                className={[
                  "h-8 rounded-md flex items-center justify-center border",
                  d
                    ? "text-stone-800 border-stone-200 bg-white"
                    : "text-transparent border-transparent bg-transparent",
                  isToday ? "!bg-amber-700 !text-white !border-amber-700 font-semibold" : "",
                ].join(" ")}
              >
                {d ?? "."}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const url =
          selectedCategory === "all"
            ? `${API_URL}/blog`
            : `${API_URL}/blog?category=${selectedCategory}`;

        const res = await fetch(url);
        const data = await res.json().catch(() => []);
        setPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error loading blog posts:", err);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [selectedCategory]);

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden min-h-[360px] md:min-h-[440px]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="GLGIF blog hero"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMAGE;
            }}
          />
        </div>
        <div className="absolute inset-0 bg-stone-950/65" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative z-10 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="font-accent text-lg md:text-xl text-amber-300 mb-3">Our Stories</p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-5 leading-tight">
              GLGIF Blog
            </h1>
            <p className="text-base md:text-lg text-stone-200 max-w-2xl">
              Stories of impact and updates from the field.
            </p>
          </div>
        </div>
      </section>

      <section className="py-4 border-b border-stone-200 sticky top-0 md:top-20 bg-white/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors border ${
                  selectedCategory === cat.value
                    ? "bg-amber-700 text-white border-amber-700"
                    : "bg-white text-stone-800 border-stone-300 hover:bg-stone-100"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-stone-100 rounded-xl h-96 animate-pulse" />
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => {
                const target = post.slug || post.id;
                return (
                  <Card key={post.id} className="overflow-hidden group rounded-2xl border-stone-200">
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={normalizeImageUrl(post.image_url)}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-amber-700 text-white hover:bg-amber-700 border border-amber-800">
                        {getCategoryLabel(post.category)}
                      </Badge>
                    </div>

                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-stone-600 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {post.views ?? 0} views
                        </span>
                      </div>

                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-3 group-hover:text-amber-700 transition-colors">
                        {post.title}
                      </h3>

                      <p className="text-stone-700 text-sm mb-4 line-clamp-2">{post.excerpt}</p>

                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-2 text-sm text-stone-600">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                        <Link
                          to={`/blog/${target}`}
                          className="text-amber-700 font-semibold hover:text-amber-800 flex items-center"
                        >
                          Read <ArrowRight className="ml-1 w-4 h-4" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <p className="text-stone-600">No blog posts found.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export const BlogPost = () => {
  const params = useParams();
  const slugOrId = params.slug || params.id;

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [latestPosts, setLatestPosts] = useState([]);

  const postImage = useMemo(() => normalizeImageUrl(post?.image_url), [post?.image_url]);

  const allTags = useMemo(() => {
    const pool = [];
    if (post?.tags?.length) pool.push(...post.tags);
    relatedPosts.forEach((p) => {
      if (Array.isArray(p.tags)) pool.push(...p.tags);
    });
    const cleaned = pool.map((t) => String(t || "").trim()).filter(Boolean);
    return Array.from(new Set(cleaned)).slice(0, 20);
  }, [post?.tags, relatedPosts]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        if (!slugOrId) throw new Error("Missing post identifier");

        const res = await fetch(`${API_URL}/blog/${slugOrId}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setPost(data);

        const relRes = await fetch(`${API_URL}/blog?category=${data.category}&limit=8`);
        const relData = await relRes.json().catch(() => []);
        const cleanRel = (Array.isArray(relData) ? relData : []).filter((p) => p.id !== data.id);
        setRelatedPosts(cleanRel.slice(0, 4));

        const latestRes = await fetch(`${API_URL}/blog?limit=8`);
        const latestData = await latestRes.json().catch(() => []);
        setLatestPosts(
          (Array.isArray(latestData) ? latestData : [])
            .filter((p) => p.id !== data.id)
            .slice(0, 6)
        );

        const cRes = await fetch(`${API_URL}/blog/${data.id}/comments`);
        const cData = await cRes.json().catch(() => []);
        setComments(Array.isArray(cData) ? cData : []);
      } catch (err) {
        console.error("Error loading blog post:", err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [slugOrId]);

  const filteredLatestPosts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return latestPosts;
    return latestPosts.filter((p) => {
      const t = String(p.title || "").toLowerCase();
      const e = String(p.excerpt || "").toLowerCase();
      const a = String(p.author || "").toLowerCase();
      return t.includes(term) || e.includes(term) || a.includes(term);
    });
  }, [latestPosts, searchTerm]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!post?.id) return;

    setSubmitting(true);
    setInfo("");

    try {
      const res = await fetch(`${API_URL}/blog/${post.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          message: message.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");

      setName("");
      setMessage("");
      setInfo("Comment submitted. It will appear after admin approval.");
    } catch (err) {
      setInfo(err?.message || "Failed to submit comment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        <Button asChild>
          <Link to="/blog">Back to Blog</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="relative isolate overflow-hidden min-h-[420px] md:min-h-[500px]">
        <div className="absolute inset-0">
          <img src={postImage} alt={post.title} className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/70 to-black/85" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.24),transparent_45%)]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          <Badge className="bg-amber-700 text-white hover:bg-amber-700 mb-4 border border-amber-800">
            {getCategoryLabel(post.category)}
          </Badge>

          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold mb-6 text-white leading-tight max-w-4xl">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-5 text-stone-100">
            <span className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {post.views ?? 0} views
            </span>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_330px] gap-8 xl:gap-10">
          <div className="min-w-0">
            <Card className="rounded-2xl border-stone-300 shadow-sm">
              <CardContent className="p-6 md:p-8">
                <article
                  className="
                    prose prose-stone prose-lg max-w-none
                    prose-headings:font-heading prose-headings:text-stone-900
                    prose-p:text-stone-800 prose-p:leading-8
                    prose-li:text-stone-800 prose-li:leading-8
                    prose-a:text-amber-700 hover:prose-a:text-amber-800
                  "
                  style={{ textAlign: "justify" }}
                  dangerouslySetInnerHTML={{ __html: post.content || "" }}
                />

                {post.tags?.length > 0 && (
                  <div className="mt-10 pt-6 border-t border-stone-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="w-5 h-5 text-stone-500" />
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-300 shadow-sm mt-8">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-amber-700" />
                  Comments
                </h3>

                <form
                  onSubmit={submitComment}
                  className="space-y-3 mb-8 bg-stone-50 border border-stone-300 rounded-2xl p-4"
                >
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-xl border border-stone-400 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-400/60"
                    required
                  />
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder="Your comment"
                    className="w-full rounded-xl border border-stone-400 px-3 py-2 outline-none focus:ring-2 focus:ring-amber-400/60"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-amber-700 hover:bg-amber-800 rounded-xl"
                  >
                    {submitting ? "Submitting..." : "Submit Comment"}
                  </Button>
                  {info ? <p className="text-sm text-stone-700">{info}</p> : null}
                </form>

                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <p className="text-stone-600">No approved comments yet.</p>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="border border-stone-300 rounded-xl p-4 bg-white">
                        <p className="font-semibold text-stone-900">{c.name}</p>
                        <p className="text-xs text-stone-600 mb-1">
                          {new Date(c.created_at).toLocaleString()}
                        </p>
                        <p className="text-stone-800 whitespace-pre-wrap">{c.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <aside className="space-y-5">
            <Card className="rounded-2xl border-stone-300 shadow-sm">
              <CardContent className="p-4">
                <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <Search className="w-4 h-4 text-amber-700" />
                  Search Posts
                </h4>
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full rounded-xl border border-stone-400 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400/60"
                />

                <div className="mt-3 space-y-2 max-h-56 overflow-y-auto pr-1">
                  {filteredLatestPosts.slice(0, 5).length === 0 ? (
                    <p className="text-sm text-stone-600">No results.</p>
                  ) : (
                    filteredLatestPosts.slice(0, 5).map((p) => (
                      <Link
                        key={p.id}
                        to={`/blog/${p.slug || p.id}`}
                        className="block text-sm text-stone-800 hover:text-amber-700 font-medium"
                      >
                        <span className="line-clamp-2">{p.title}</span>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-300 shadow-sm">
              <CardContent className="p-4">
                <h4 className="font-semibold text-stone-900 mb-3">Categories</h4>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.filter((c) => c.value !== "all").map((cat) => (
                    <Link key={cat.value} to={`/blog?category=${cat.value}`}>
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-stone-900 text-white border border-stone-900 hover:bg-amber-700 hover:border-amber-700 transition-colors">
                        {cat.label}
                      </span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-300 shadow-sm">
              <CardContent className="p-4">
                <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-amber-700" />
                  Tags
                </h4>
                {allTags.length === 0 ? (
                  <p className="text-sm text-stone-600">No tags yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-300 shadow-sm">
              <CardContent className="p-4">
                <h4 className="font-semibold text-stone-900 mb-3">Related Posts</h4>
                <div className="space-y-3">
                  {relatedPosts.length === 0 ? (
                    <p className="text-sm text-stone-600">No related posts.</p>
                  ) : (
                    relatedPosts.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/blog/${rel.slug || rel.id}`}
                        className="flex gap-3 group"
                      >
                        <img
                          src={normalizeImageUrl(rel.image_url)}
                          alt={rel.title}
                          className="w-20 h-16 rounded-lg object-cover border border-stone-300"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-stone-900 group-hover:text-amber-700 line-clamp-2">
                            {rel.title}
                          </p>
                          <p className="text-xs text-stone-600 mt-1">
                            {formatDate(rel.created_at)}
                          </p>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <MiniCalendar />
          </aside>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="py-14 bg-stone-50 border-t border-stone-200">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-heading text-2xl font-bold text-stone-900 mb-8">
              More in {getCategoryLabel(post.category)}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedPosts.slice(0, 2).map((relPost) => (
                <Card key={relPost.id} className="overflow-hidden rounded-2xl border-stone-300 shadow-sm">
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={normalizeImageUrl(relPost.image_url)}
                      alt={relPost.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-bold text-stone-900 mb-2">
                      {relPost.title}
                    </h3>
                    <p className="text-sm text-stone-700 mb-4 line-clamp-2">{relPost.excerpt}</p>
                    <Link
                      to={`/blog/${relPost.slug || relPost.id}`}
                      className="text-amber-700 font-semibold hover:text-amber-800 flex items-center"
                    >
                      Read More <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogList;
